import { createFileRoute, Link } from "@tanstack/react-router";

const linkCls = "underline! decoration-[#f5cc00]! decoration-2! underline-offset-4 hover:text-[#1a1408]";

const pageTitle = "Salesforce SELA Procurement & Contract Review | SaaSed.co";
const pageDesc =
  "Independent Salesforce SELA procurement and contract review for enterprises preparing to sign, renew or restructure a multi-year agreement.";

const faqData = [
  {
    question: "What is a Salesforce SELA contract?",
    answer:
      "A Salesforce Enterprise License Agreement (SELA) is a long-term, enterprise-wide commercial arrangement that bundles multiple Salesforce products and entitlements into a single contract. Instead of purchasing individual SKUs separately, the organisation gains broader access to a defined portfolio of products — often with volume-based or consumption-driven pricing.",
  },
  {
    question: "How is a SELA different from a standard Salesforce agreement?",
    answer:
      "A standard Salesforce agreement is typically SKU-specific: you buy Sales Cloud, Service Cloud, or Marketing Cloud as discrete products with fixed user counts. A SELA replaces this granular structure with a single enterprise-wide commitment. The pitch is flexibility — but the reality depends on how closely the bundled entitlements match your actual usage patterns and product roadmap.",
  },
  {
    question: "What are the main benefits of a Salesforce SELA?",
    answer:
      "The benefits include consolidated purchasing power, potential volume discounts, reduced administrative overhead per SKU, the ability to add products within an agreed scope without renegotiating each line item, and multi-year budget predictability. For organisations with genuine economies of scale and complex multi-product implementations, this structure can simplify procurement.",
  },
  {
    question: "What are the risks of signing a Salesforce SELA?",
    answer:
      "The risks include paying for entitlements you never consume, bundled pricing that obscures true per-product cost, deep lock-in that weakens your leverage at renewal, Salesforce roadmap changes that may not align with your needs, and the complexity of tracking actual consumption against entitlement. The 'flexibility' narrative often serves Salesforce's revenue growth more than your commercial position.",
  },
  {
    question: "How to negotiate a Salesforce SELA?",
    answer:
      "Start with a disciplined assessment of current and projected usage across every product line. Demand unbundled pricing visibility so you can compare per-SKU costs against standalone rates. Negotiate clear unbundling and true-up terms before you sign, not at renewal. Channel all communications through a single point of contact to prevent Salesforce from exploiting internal silos. Most importantly, treat the negotiation as a long-term commercial strategy, not a one-time transaction.",
  },
  {
    question: "When should an organization consider a SELA over standard licensing?",
    answer:
      "A SELA makes sense when your organisation has genuine economies of scale, unpredictable usage patterns that benefit from pooled entitlements, or a complex multi-product implementation where SKU-by-SKU administration becomes a material burden. It does not make sense when the primary driver is an account executive's quarterly target or an internal champion's desire for access to more products without a clear business case.",
  },
  {
    question: "Can a Salesforce SELA be renegotiated or unbundled?",
    answer:
      "Yes, but it is significantly harder than renegotiating a standard agreement. The bundled structure creates commercial interdependencies: removing one product can trigger repricing across the entire agreement. If you are already in a SELA and considering unbundling, treat it as a full renegotiation. Prepare detailed usage data, benchmark pricing, and a clear alternative structure before entering discussions.",
  },
];

const jsonLd = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "FAQPage",
      mainEntity: faqData.map((item) => ({
        "@type": "Question",
        name: item.question,
        acceptedAnswer: {
          "@type": "Answer",
          text: item.answer,
        },
      })),
    },
    {
      "@type": "Service",
      name: "Salesforce SELA Advisory",
      provider: {
        "@type": "Organization",
        name: "SaaSed",
        url: "https://saased.co",
      },
      description:
        "Specialist procurement support to assess, negotiate, and optimise Salesforce Enterprise License Agreements.",
      areaServed: "EMEA",
      serviceType: "Software Procurement Consulting",
    },
  ],
};

function StrategyCta({
  label = "REQUEST A COMPLIMENTARY ASSESSMENT",
  context = "primary",
}: {
  label?: string;
  context?: "primary" | "dark";
}) {
  const base =
    "font-sans text-sm font-semibold uppercase tracking-[0.18em] px-8 py-4 min-h-[52px] transition-colors inline-flex items-center gap-3";

  const variants = {
    primary: "bg-brand-primary text-brand-ink hover:bg-brand-primary/90",
    dark: "bg-primary text-primary-foreground hover:bg-primary/90",
  };
  return (
    <Link to="/contact" className={`${base} ${variants[context]}`}>
      {label}
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <path d="M5 12h14" />
        <path d="m12 5 7 7-7 7" />
      </svg>
    </Link>
  );
}

export const Route = createFileRoute("/sela")({
  head: () => ({
    meta: [
      { title: pageTitle },
      { name: "description", content: pageDesc },
      { property: "og:title", content: pageTitle },
      { property: "og:description", content: pageDesc },
      { property: "og:type", content: "article" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: pageTitle },
      { name: "twitter:description", content: pageDesc },
      { property: "og:url", content: "https://saased.co/sela" },
    ],
    links: [{ rel: "canonical", href: "https://saased.co/sela" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify(jsonLd),
      },
    ],
  }),
  component: SelaPage,
});

function SelaPage() {
  return (
    <>
      {/* HERO */}
      <section className="border-b border-brand-ink/10 bg-brand-primary py-16 md:py-20 px-6 md:px-12">
        <div className="max-w-7xl mx-auto flex flex-col gap-6 md:gap-8">
          <p className="font-sans text-xs font-medium uppercase tracking-[0.2em] text-brand-ink/70">
            Salesforce Contracts
          </p>
          <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight max-w-4xl">
            Enterprise Salesforce SELA Procurement & Cost Optimization
          </h1>
          <div className="max-w-3xl space-y-6 font-sans text-base leading-relaxed text-brand-ink/85">
            <p>
              A Salesforce Enterprise License Agreement (SELA) is a long-term,
              enterprise-wide commercial arrangement. It bundles multiple
              Salesforce products and entitlements into a single contract.
            </p>
            <p>
              The pitch is simplicity and flexibility. The commercial reality
              depends on how closely the agreement matches your actual usage,
              roadmap, and procurement discipline.
            </p>
            <p>
              For organisations with genuine economies of scale and complex
              multi-product implementations, a SELA can reduce administrative
              burden and create budget predictability.
            </p>
            <p>
              For others, it becomes a mechanism for paying for products they
              never deploy, at prices they cannot easily benchmark, inside a
              structure that is deliberately hard to unwind.
            </p>
          </div>
        </div>

        <div className="mt-12 max-w-7xl mx-auto contain-layout">
          <div className="bg-brand-ink rounded-2xl p-8 md:p-12 lg:p-14 max-w-4xl shadow-glow">
            <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl leading-[1.05] tracking-tight text-brand-ivory max-w-2xl whitespace-pre-line">
              Planning, Negotiating, or Renewing a Salesforce SELA?{"\n\n"}
            </h2>
            <p className="mt-5 font-sans text-base md:text-lg leading-relaxed text-brand-sand max-w-2xl">
              Get an independent contract assessment before you commit. We review your SELA commercials, identify hidden risks, and give you a clear negotiating position.
            </p>
            <div className="mt-8">
              <Link
                to="/contact"
                className="font-sans text-sm font-semibold uppercase tracking-[0.18em] px-8 py-4 min-h-[56px] min-w-[280px] transition-colors inline-flex items-center justify-center gap-3 bg-brand-primary text-brand-ink hover:bg-brand-primary/90 rounded-sm"
              >
                Request a SELA Contract Assessment
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="M5 12h14" />
                  <path d="m12 5 7 7-7 7" />
                </svg>
              </Link>
            </div>
            <div className="mt-8 grid gap-4 sm:grid-cols-3">
              <div className="flex items-start gap-3">
                <span className="mt-1 h-2 w-2 shrink-0 rounded-full bg-brand-primary" />
                <p className="font-sans text-sm leading-snug text-brand-primary">
                  Independent pricing benchmark
                </p>
              </div>
              <div className="flex items-start gap-3">
                <span className="mt-1 h-2 w-2 shrink-0 rounded-full bg-brand-primary" />
                <p className="font-sans text-sm leading-snug text-brand-primary">
                  Hidden-risk identification
                </p>
              </div>
              <div className="flex items-start gap-3">
                <span className="mt-1 h-2 w-2 shrink-0 rounded-full bg-brand-primary" />
                <p className="font-sans text-sm leading-snug text-brand-primary">
                  Renewal leverage plan
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* WHAT IS A SELA */}
      <section className="border-b border-brand-ink/10 bg-brand-cream">
        <div className="container-x py-16 md:py-20 max-w-4xl">
          <div className="flex flex-col gap-6">
            <p className="font-sans text-xs font-medium uppercase tracking-[0.2em] text-brand-ink/70">
              01 · Definition
            </p>
            <h2 className="font-serif text-2xl md:text-3xl leading-tight">
              What Is a Salesforce SELA Contract?
            </h2>
          </div>
          <div className="mt-12 space-y-5 font-sans text-base leading-relaxed text-brand-ink/85">
            <p style={{ marginBottom: '24px' }}>
              A SELA replaces the standard SKU-by-SKU purchasing model with a
              single enterprise-wide commitment. Instead of buying Sales Cloud,
              Service Cloud, or Marketing Cloud separately, the organisation
              gains access to a defined portfolio under one agreement.
            </p>
            <p style={{ marginBottom: '24px' }}>
              Pricing is typically volume-based or consumption-driven. The
              structure is designed to give procurement teams fewer order forms
              to manage and more predictable multi-year spend.
            </p>
            <p>
              That structure can be valuable. But it can also obscure the true
              cost of each product, making it harder to benchmark whether the
              deal is genuinely competitive.
            </p>
          </div>
        </div>
      </section>

      {/* BENEFITS */}
      <section className="border-b border-brand-ink/10 bg-brand-cream">
        <div className="container-x py-16 md:py-20">
          <div className="flex flex-col gap-6">
            <p className="font-sans text-xs font-medium uppercase tracking-[0.2em] text-brand-ink/70">
              02 · Benefits
            </p>
            <h2 className="font-serif text-2xl md:text-3xl leading-tight">
              Why a SELA Can Make Sense
            </h2>
          </div>
          <div className="grid md:grid-cols-2 gap-12 md:gap-16 mt-12">
            <div className="flex flex-col gap-6">
              <h3 className="font-serif text-xl md:text-2xl leading-tight">
                Commercial Consolidation
              </h3>
              <ul className="flex flex-col gap-6 font-sans text-base leading-relaxed text-brand-ink/85">
                <li className="flex gap-3">
                  <span className="text-brand-punch mt-1">•</span>
                  <span>
                    <strong>Consolidated purchasing power</strong> across
                    multiple products.
                  </span>
                </li>
                <li className="flex gap-3">
                  <span className="text-brand-punch mt-1">•</span>
                  <span>
                    <strong>Potential volume discounts</strong> when total
                    spend reaches meaningful thresholds.
                  </span>
                </li>
                <li className="flex gap-3">
                  <span className="text-brand-punch mt-1">•</span>
                  <span>
                    <strong>Reduced administrative overhead</strong> — fewer
                    renewals, fewer order forms, less procurement friction.
                  </span>
                </li>
              </ul>
            </div>
            <div className="flex flex-col gap-6">
              <h3 className="font-serif text-xl md:text-2xl leading-tight">
                Operational Flexibility
              </h3>
              <ul className="flex flex-col gap-6 font-sans text-base leading-relaxed text-brand-ink/85">
                <li className="flex gap-3">
                  <span className="text-brand-punch mt-1">•</span>
                  <span>
                    <strong>Ability to add products</strong> within an agreed
                    scope without renegotiating each line item.
                  </span>
                </li>
                <li className="flex gap-3">
                  <span className="text-brand-punch mt-1">•</span>
                  <span>
                    <strong>Multi-year budget predictability</strong> for
                    finance teams planning long-term software investment.
                  </span>
                </li>
                <li className="flex gap-3">
                  <span className="text-brand-punch mt-1">•</span>
                  <span>
                    <strong>Simpler vendor management</strong> when a single
                    agreement replaces dozens of discrete contracts.
                  </span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* RISKS */}
      <section className="border-b border-brand-ink/10 bg-brand-cream">
        <div className="container-x py-16 md:py-20">
          <div className="flex flex-col gap-6">
            <p className="font-sans text-xs font-medium uppercase tracking-[0.2em] text-brand-ink/70">
              03 · Risks
            </p>
            <h2 className="font-serif text-2xl md:text-3xl leading-tight">
              Where a SELA Works Against You
            </h2>
          </div>
          <div className="grid md:grid-cols-2 gap-12 md:gap-16 mt-12">
            <div className="flex flex-col gap-6">
              <h3 className="font-serif text-xl md:text-2xl leading-tight">
                Hidden Costs
              </h3>
              <ul className="flex flex-col gap-6 font-sans text-base leading-relaxed text-brand-ink/85">
                <li className="flex gap-3">
                  <span className="text-brand-punch mt-1">•</span>
                  <span>
                    <strong>Paying for entitlements you never consume</strong>{" "}
                    — bundled access sounds generous until much of it sits
                    unused.
                  </span>
                </li>
                <li className="flex gap-3">
                  <span className="text-brand-punch mt-1">•</span>
                  <span>
                    <strong>Bundled pricing hides true per-product cost</strong>,
                    making benchmarking nearly impossible.
                  </span>
                </li>
                <li className="flex gap-3">
                  <span className="text-brand-punch mt-1">•</span>
                  <span>
                    <strong>True-up surprises</strong> when usage exceeds pooled
                    entitlements at punitive rates.
                  </span>
                </li>
              </ul>
            </div>
            <div className="flex flex-col gap-6">
              <h3 className="font-serif text-xl md:text-2xl leading-tight">
                Strategic Lock-In
              </h3>
              <ul className="flex flex-col gap-6 font-sans text-base leading-relaxed text-brand-ink/85">
                <li className="flex gap-3">
                  <span className="text-brand-punch mt-1">•</span>
                  <span>
                    <strong>Deep lock-in weakens renewal leverage</strong> once
                    Salesforce knows unbundling would be painful.
                  </span>
                </li>
                <li className="flex gap-3">
                  <span className="text-brand-punch mt-1">•</span>
                  <span>
                    <strong>Roadmap changes outside your control</strong> may
                    not align with your business priorities.
                  </span>
                </li>
                <li className="flex gap-3">
                  <span className="text-brand-punch mt-1">•</span>
                  <span>
                    <strong>The ‘flexibility’ narrative</strong> often serves
                    Salesforce's revenue growth more than your commercial
                    position.
                  </span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* MID-PAGE CTA */}
      <section className="border-b border-brand-ink/10 bg-brand-cream py-16 md:py-20 px-6">
        <div className="max-w-3xl mx-auto flex flex-col items-center text-center gap-6 md:gap-8">
          <h2 className="font-serif text-2xl md:text-3xl leading-tight">
            Ready to Optimise Your Salesforce SELA Commercials?
          </h2>
          <p className="font-sans text-base leading-relaxed text-brand-ink/85">
            Get an independent, expert review of your contract before you sign, renew, or restructure.
          </p>
          <StrategyCta label="REQUEST A COMPLIMENTARY ASSESSMENT" />
        </div>
      </section>

      {/* ASSESSMENT */}
      <section className="border-b border-brand-ink/10 bg-brand-cream">
        <div className="container-x py-16 md:py-20 max-w-4xl">
          <div className="flex flex-col gap-6">
            <p className="font-sans text-xs font-medium uppercase tracking-[0.2em] text-brand-ink/70">
              04 · Assessment
            </p>
            <h2 className="font-serif text-2xl md:text-3xl leading-tight">
              Does a SELA Genuinely Fit Your Organisation?
            </h2>
            <div className="flex flex-col gap-6 font-sans text-base leading-relaxed text-brand-ink/85">
              <p>
                The decision to pursue a Salesforce SELA should not be driven by
                an account executive’s quarterly target. It should be driven by
                data.
              </p>
              <p>Before entering discussions, answer three questions honestly:</p>
              <ul className="space-y-4 pl-1">
                <li className="flex gap-3">
                  <span className="text-brand-punch mt-1">•</span>
                  <span>
                    <strong>Are the economies of scale real?</strong> Compare
                    bundled pricing against standalone rates for the products you
                    actually use.
                  </span>
                </li>
                <li className="flex gap-3">
                  <span className="text-brand-punch mt-1">•</span>
                  <span>
                    <strong>Would pay-as-you-go be cheaper?</strong> Slightly more
                    administration can be worth materially better commercial
                    outcomes.
                  </span>
                </li>
                <li className="flex gap-3">
                  <span className="text-brand-punch mt-1">•</span>
                  <span>
                    <strong>What happens if you need to unbundle?</strong> The
                    time to negotiate exit terms is before you sign, not at
                    renewal.
                  </span>
                </li>
              </ul>
              <p>
                Most organisations that overpay for Salesforce do so not because
                they chose the wrong product structure, but because they accepted
                the vendor’s narrative without independent validation.
              </p>
              <p>
                A SELA is a tool. Whether it is the right tool depends on who is
                holding it — and whether they are negotiating from a position of
                knowledge, not pressure.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* DEEP DIVE RESOURCES */}
      <section className="border-b border-brand-ink/10 bg-brand-primary">
        <div className="container-x py-16 md:py-20">
          <div className="flex flex-col gap-6 mb-10">
            <h2 className="font-serif text-2xl md:text-3xl leading-tight">
              SELA Enterprise Insights
            </h2>
            <p className="font-sans text-base leading-relaxed text-brand-ink/85 max-w-3xl">
              Discover our comprehensive analysis and practical guides to master your next Salesforce SELA negotiation. For agentic AI agreements, see our{" "}
              <Link to="/aela" className="underline decoration-2 underline-offset-4 hover:text-brand-punch">Salesforce AELA advisory</Link>, or review the full range of our{" "}
              <Link to="/services" className="underline decoration-2 underline-offset-4 hover:text-brand-punch">Salesforce procurement and negotiation services</Link>. A SELA is one of several structures covered in our{" "}
              <Link to="/salesforce-procurement" className="underline decoration-2 underline-offset-4 hover:text-brand-punch">Salesforce procurement and commercial advisory</Link> overview.
            </p>

          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 lg:gap-6">
            {[
              {
                title: "What is Actually a Salesforce SELA?",
                slug: "what-is-actually-a-salesforce-sela",
              },
              {
                title: "How Salesforce SELA Pricing Models Affect Renewal Costs",
                slug: "how-salesforce-sela-pricing-models-affect-renewal-costs",
              },
              {
                title: "Salesforce SELA: Avoiding Bundling Traps & Securing Decoupled Pricing",
                slug: "salesforce-sela-avoiding-bundling-traps-and-securing-decoupled-pricing",
              },
              {
                title: "Salesforce SELA Changes: Negotiation Tactics Demystified",
                slug: "salesforce-sela-changes-negotiation-tactics-for",
              },
              {
                title: "Salesforce SELA vs. AELA: Navigating the Agentic AI Cost Paradigm",
                slug: "salesforce-sela-vs-aela-navigating-the-agentic-ai-cost-paradigm",
              },
              {
                title: "Salesforce Commercial Structures: Standard Agreement vs. SELA vs. AELA",
                slug: "salesforce-commercial-structures-standard-agreement-vs-sela-vs-aela",
              },
            ].map((article) => (
              <Link
                key={article.slug}
                to="/blog/$slug"
                params={{ slug: article.slug }}
                search={{}}
                title={article.title}
                aria-label={`Read ${article.title}`}
                className="group flex h-full flex-col justify-between gap-6 rounded-2xl border border-brand-ink/10 bg-brand-cream p-8 transition-colors hover:border-brand-punch/40"
              >
                <h3 className="font-serif text-xl leading-tight text-brand-ink">
                  {article.title}
                </h3>
                <span className="inline-flex items-center gap-2 font-sans text-xs font-medium uppercase tracking-[0.18em] text-brand-ink/70 group-hover:text-brand-punch">
                  Read the analysis
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <path d="M5 12h14" />
                    <path d="m12 5 7 7-7 7" />
                  </svg>
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="border-b border-brand-ink/10 bg-[#f0e8c8]">
        <div className="container-x py-24 md:py-28">
          <p className="text-center font-sans text-xs font-medium uppercase tracking-[0.2em] text-brand-ink/70" style={{ marginBottom: "16px" }}>
            05 · FAQ
          </p>
          <h2 className="m-0 text-center font-serif text-3xl font-bold text-[#1a1408] md:text-4xl">
            FAQ about Salesforce SELA Contracts
          </h2>
          <div aria-hidden="true" className="mx-auto mt-4 h-[2px] w-16 bg-[#f5cc00]" />
          <div className="mx-auto mt-12 flex max-w-3xl flex-col gap-4">
            {faqData.map((f, i) => (
              <details key={f.question} open={i === 0} className="group overflow-hidden rounded-2xl border border-[#f0e8c8] bg-[#fffdf0] shadow-[0_1px_2px_rgba(26,20,8,0.06)] transition-colors duration-150 hover:border-[#1a1408]/30">
                <summary className="flex cursor-pointer list-none items-center justify-between gap-4 px-6 py-5 md:px-8 md:py-6 rounded-2xl focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#1a1408] [&::-webkit-details-marker]:hidden">
                  <h3 className="m-0 font-sans text-lg font-medium leading-[1.35] tracking-normal text-[#1a1408] md:text-xl">{f.question}</h3>
                  <span aria-hidden="true" className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#f0e8c8] transition-colors duration-200 group-open:bg-[#f5cc00]">
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" className="transition-transform duration-200 group-open:rotate-45"><path d="M7 1v12M1 7h12" stroke="#1a1408" strokeWidth="2" strokeLinecap="round" /></svg>
                  </span>
                </summary>
                <div className="flex flex-col gap-4 border-t border-[#f0e8c8] px-6 pb-6 pt-5 md:px-8 md:pb-8">
                  <p className="m-0 text-left text-base leading-[1.7] text-[#1a1408]/85">{f.answer}</p>
                </div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* END CTA */}
      <section className="bg-brand-ink py-16 md:py-24 px-6 md:px-12 [contain:layout] min-h-[560px] md:min-h-[620px] lg:min-h-[680px] [content-visibility:auto] [contain-intrinsic-size:680px]">
        <div className="container-x !px-0">
          <div className="max-w-3xl flex flex-col items-start gap-6 md:gap-8">
            <p className="font-sans text-xs font-medium uppercase tracking-[0.2em] text-brand-primary min-h-[16px]">
              Next Step
            </p>
            <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl leading-tight text-brand-ivory min-h-[3.5rem] md:min-h-[4.5rem] lg:min-h-[7rem]">
              Ready to Optimise Your Salesforce SELA Commercials?
            </h2>
            <p className="font-sans text-lg md:text-xl leading-relaxed text-brand-sand max-w-2xl">
              SaaSed is a specialist Salesforce procurement and commercial advisory company. We work with enterprise buyers on Salesforce renewals, contract negotiation, licensing decisions, commercial strategy and cost optimisation — with the aim of improving commercial control and removing cost that serves no purpose.
            </p>
            <p className="font-sans text-lg md:text-xl leading-relaxed text-brand-sand max-w-2xl min-h-[3.5rem]">
              Get an independent, expert review of your contract before you sign, renew, or restructure.
            </p>
            <Link
              to="/contact"
              title="REQUEST A COMPLIMENTARY ASSESSMENT"
              aria-label="REQUEST A COMPLIMENTARY ASSESSMENT"
              className="font-sans text-sm font-semibold uppercase tracking-[0.18em] px-8 py-4 min-h-[52px] transition-colors inline-flex items-center gap-3 bg-brand-primary text-brand-ink hover:bg-brand-primary/90"
            >
              REQUEST A COMPLIMENTARY ASSESSMENT
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M5 12h14" />
                <path d="m12 5 7 7-7 7" />
              </svg>
            </Link>
          </div>
        </div>
      </section>

      {/* RELATED */}
      <nav aria-label="Related" className="container-x flex flex-col items-center gap-4 px-6 py-12 text-center md:px-12">
        <p className="font-sans text-[11px] font-medium uppercase tracking-[0.12em] text-muted-foreground">RELATED</p>
        <div className="flex flex-wrap justify-center gap-6 font-sans">
          <Link to="/dioro" className={linkCls}>Dioro, our Salesforce analytics tool</Link>
          <Link to="/case-study" className={linkCls}>PHMG case study</Link>
          <Link to="/blog/$slug" params={{ slug: "when-does-your-salesforce-renewal-actually-start" }} className={linkCls}>When does your Salesforce renewal actually start?</Link>
        </div>
      </nav>


    </>
  );
}
